class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Initialize mobile cart overlay
    this.mobileCartOverlay = document.getElementById('mobile-cart-overlay');
    this.mobileCartOverlayCount = document.getElementById('mobile-cart-overlay-count');
    this.mobileSuccessBanner = document.getElementById('mobile-cart-success-banner-drawer');
    this.isMobile = window.innerWidth < 750;
    this.cartUpdateUnsubscriber = undefined;
    
    // Handle window resize to update mobile detection
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 750;
    });
    
    // Ensure success banner is hidden on page load/refresh
    if (this.mobileSuccessBanner) {
      this.mobileSuccessBanner.setAttribute('aria-hidden', 'true');
    }

    // Ensure overlay is hidden on page load until we check cart state
    if (this.mobileCartOverlay) {
      this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
    }
    
    // Subscribe to cart updates to keep mobile overlay in sync
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Always update mobile cart overlay when cart updates
        // If cartData is provided, use it; otherwise fetch fresh data
        if (event.cartData) {
          this.updateMobileCartOverlay(event.cartData);
        } else {
          // Fetch fresh cart data if not provided in event
          this.initializeMobileCartOverlay();
        }
      });
    }
    
    // Initialize mobile cart overlay on page load (with delay to prevent flash)
    setTimeout(() => {
      this.initializeMobileCartOverlay();
    }, 100);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async initializeMobileCartOverlay() {
    // Check if we're on the cart page - don't show overlay on cart page
    const isCartPage = window.location.pathname === routes.cart_url || 
                       window.location.pathname.includes('/cart') ||
                       document.body.classList.contains('template-cart');
    
    // Check if we're on product page (PDP) or collection page (PLP)
    const isProductPage = document.body.classList.contains('template-product') ||
                          window.location.pathname.includes('/products/');
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                             window.location.pathname.includes('/collections/');
    const isHomePage = document.body.classList.contains('template-index') ||
    window.location.pathname === '/' ||
    window.location.pathname === '';
    const isPDPOrPLPOrHome = isProductPage || isCollectionPage || isHomePage;
    
    // Hide overlay if not on PDP/PLP or on cart page
    if (isCartPage || !isPDPOrPLPOrHome) {
      if (this.mobileCartOverlay) {
        this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
      }
      return;
    }
    
    // Fetch current cart state to initialize overlay
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (response.ok) {
        const cartData = await response.json();
        // Only update if cart has items, otherwise keep it hidden
        if (cartData.item_count > 0) {
          this.updateMobileCartOverlay(cartData);
        } else {
          // Ensure overlay is hidden if cart is empty
          if (this.mobileCartOverlay) {
            this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      // Hide overlay on error
      if (this.mobileCartOverlay) {
        this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
      }
    }
  }

  showMobileSuccessBanner() {
    if (!this.mobileSuccessBanner) return;
    
    this.mobileSuccessBanner.setAttribute('aria-hidden', 'false');
    
    // Auto-hide after 3 seconds
    clearTimeout(this.mobileBannerTimeout);
    this.mobileBannerTimeout = setTimeout(() => {
      this.hideMobileSuccessBanner();
    }, 3000);
  }

  hideMobileSuccessBanner() {
    if (!this.mobileSuccessBanner) return;
    this.mobileSuccessBanner.setAttribute('aria-hidden', 'true');
  }

  async updateMobileCartOverlay(cartData) {
    // Re-fetch elements in case they weren't available during construction
    if (!this.mobileCartOverlay) {
      this.mobileCartOverlay = document.getElementById('mobile-cart-overlay');
    }
    if (!this.mobileCartOverlayCount) {
      this.mobileCartOverlayCount = document.getElementById('mobile-cart-overlay-count');
    }
    
    if (!this.mobileCartOverlay || !this.mobileCartOverlayCount) {
      return;
    }
    
    // Fetch full cart data if not provided or incomplete
    let fullCartData = cartData;
    if (!cartData?.item_count && (!cartData?.items || cartData.items.length === 0)) {
      try {
        const response = await fetch(`${routes.cart_url}.js`);
        if (response.ok) {
          fullCartData = await response.json();
        }
      } catch (error) {
        console.error('Error fetching cart data:', error);
        // Hide overlay if fetch fails
        this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
        return;
      }
    }
    
    const itemCount = fullCartData?.item_count || 0;
    const itemText = itemCount === 1 ? 'Item' : 'Items';
    
    this.mobileCartOverlayCount.textContent = `${itemCount} ${itemText}`;
    
    // Always update product images - this is called every time cart updates
    await this.updateMobileCartImages(fullCartData);
    
    // Check if we're on the cart page
    const isCartPage = window.location.pathname === routes.cart_url || 
                       window.location.pathname.includes('/cart') ||
                       document.body.classList.contains('template-cart');
    
    // Check if we're on product page (PDP) or collection page (PLP)
    const isProductPage = document.body.classList.contains('template-product') ||
                          window.location.pathname.includes('/products/');
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                             window.location.pathname.includes('/collections/');
    const isHomePage = document.body.classList.contains('template-index') ||
                       window.location.pathname === '/' ||
                       window.location.pathname === '';
    const isPDPOrPLPOrHome = isProductPage || isCollectionPage || isHomePage;
    
    // Show overlay ONLY if there are items in cart, on mobile, NOT on cart page, and on PDP/PLP
    // Hide immediately if cart is empty to prevent flashing
    if (itemCount > 0 && this.isMobile && !isCartPage && isPDPOrPLPOrHome) {
      this.mobileCartOverlay.setAttribute('aria-hidden', 'false');
    } else {
      this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
    }
  }

  async updateMobileCartImages(cartData) {
    console.log('updateMobileCartImages called with cartData:', cartData);
    const imagesContainer = document.getElementById('mobile-cart-overlay-images');
    if (!imagesContainer) {
      console.warn('mobile-cart-overlay-images container not found');
      return;
    }

    let items = cartData?.items || [];
    
    // If items don't have image data, fetch from cart.js
    if (items.length > 0 && !items[0].image && !items[0].featured_image) {
      try {
        const response = await fetch(`${routes.cart_url}.js`);
        if (response.ok) {
          const fullCartData = await response.json();
          items = fullCartData.items || items;
        }
      } catch (error) {
        console.error('Error fetching cart data for images:', error);
      }
    }
    
    // Get last 2 items - newest items are at the end of the array
    // Reverse to show newest first (most recently added on top)
    const recentItems = items.slice(0, 2);
    
    // Debug: Log to verify function is being called
    console.log('updateMobileCartImages called - items count:', items.length, 'recentItems:', recentItems.length);
    
    if (recentItems.length === 0) {
      imagesContainer.innerHTML = '';
      return;
    }
    
    // Get the newest item (first in reversed array) - this is the LAST item added
    const newestItem = recentItems[0];
    const newestItemId = newestItem?.variant_id || newestItem?.id || newestItem?.key;
    
    // Check if we have existing images to animate
    const existingImages = imagesContainer.querySelectorAll('.mobile-cart-overlay__image-wrapper');
    const hasExistingImages = existingImages.length > 0;
    
    // Try to detect if a new product was added by comparing variant/product IDs
    let shouldAnimate = false;
    if (hasExistingImages && newestItemId) {
      const currentFirstImage = existingImages[0];
      const currentFirstImageDataId = currentFirstImage?.getAttribute('data-variant-id');
      
      // If the newest item ID is different from current first image, animate
      if (currentFirstImageDataId && currentFirstImageDataId !== String(newestItemId)) {
        shouldAnimate = true;
      } else if (!currentFirstImageDataId) {
        // If no data attribute, try to compare by image URL
        const currentFirstImageSrc = currentFirstImage?.querySelector('img')?.src || '';
        const newestItemImageUrl = this.getImageUrl(newestItem);
        if (newestItemImageUrl) {
          // Extract filename from URLs for comparison
          const currentFilename = currentFirstImageSrc.split('/').pop().split('?')[0];
          const newestFilename = newestItemImageUrl.split('/').pop().split('?')[0];
          if (currentFilename !== newestFilename) {
            shouldAnimate = true;
          }
        }
      }
    }
    
    // If we should animate and have existing images
    if (shouldAnimate && hasExistingImages && newestItem) {
      const currentFirstImage = existingImages[0];
      
      // Animate: move current first to second position
      if (currentFirstImage) {
        currentFirstImage.classList.add('animate-to-second');
      }
      
      // Remove old second image if it exists
      if (existingImages[1]) {
        existingImages[1].style.opacity = '0';
        setTimeout(() => {
          if (existingImages[1].parentNode) {
            existingImages[1].remove();
          }
        }, 400);
      }
      
      // Create and add new first image with drop-in animation
      const newFirstImage = this.createImageElement(newestItem, 0, recentItems.length);
      if (newFirstImage) {
        newFirstImage.setAttribute('data-variant-id', String(newestItemId));
        
        // Set initial position for drop-in animation (from top)
        newFirstImage.style.transform = 'translate(0, -20rem) scale(0.4)';
        newFirstImage.style.opacity = '0';
        newFirstImage.style.transition = 'none';
        
        imagesContainer.insertBefore(newFirstImage, currentFirstImage);
        
        // Trigger drop-in animation using requestAnimationFrame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            newFirstImage.style.transition = 'transform 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55), opacity 0.7s ease, scale 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
            newFirstImage.style.transform = 'translate(0, 0) scale(1)';
            newFirstImage.style.opacity = '1';
            
            // Add bounce effect
            setTimeout(() => {
              newFirstImage.style.transition = 'transform 0.2s ease';
              newFirstImage.style.transform = 'translate(0, 0.3rem) scale(1.05)';
              setTimeout(() => {
                newFirstImage.style.transform = 'translate(0, -0.15rem) scale(0.98)';
                setTimeout(() => {
                  newFirstImage.style.transform = 'translate(0, 0) scale(1)';
                }, 100);
              }, 100);
            }, 400);
          });
        });
        
        // Clean up animation classes after animation completes
        setTimeout(() => {
          if (currentFirstImage) {
            currentFirstImage.classList.remove('animate-to-second');
          }
          newFirstImage.style.transition = '';
        }, 1000);
      }
      
      return;
    }
    
    // If no animation needed, just update normally - ensure newest is always first
    imagesContainer.innerHTML = '';
    
    // Create image elements - first item (index 0) is the newest and should appear on top
    recentItems.forEach((item, index) => {
      const imgWrapper = this.createImageElement(item, index, recentItems.length);
      if (imgWrapper) {
        const itemId = item?.variant_id || item?.id || item?.key;
        if (itemId) {
          imgWrapper.setAttribute('data-variant-id', String(itemId));
        }
        // Ensure first item (newest) has highest z-index
        if (index === 0) {
          imgWrapper.style.zIndex = '10';
        } else {
          imgWrapper.style.zIndex = '5';
        }
        imagesContainer.appendChild(imgWrapper);
      }
    });
  }

  getImageUrl(item) {
    if (item.image) {
      if (typeof item.image === 'string' && item.image.startsWith('http')) {
        return item.image;
      } else if (item.image.url) {
        return item.image.url;
      } else if (item.featured_image) {
        return item.featured_image;
      }
    } else if (item.featured_image) {
      return item.featured_image;
    }
    return null;
  }

  createImageElement(item, index, totalItems) {
    const imageUrl = this.getImageUrl(item);
    if (!imageUrl) return null;

    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'mobile-cart-overlay__image-wrapper';
    // First item (index 0) = newest = highest z-index (10)
    // Second item (index 1) = older = lower z-index (5)
    imgWrapper.style.zIndex = index === 0 ? '10' : '5';
    
    const img = document.createElement('img');
    if (imageUrl.includes('cdn.shopifycdn.com') || imageUrl.includes('cdn.shopify.com')) {
      img.src = imageUrl.replace(/(\.(jpg|jpeg|png|webp))/i, '_100x100$1');
    } else {
      img.src = imageUrl;
    }
    img.alt = item.product_title || item.title || 'Product';
    img.className = 'mobile-cart-overlay__image';
    img.loading = 'lazy';
    
    img.onerror = function() {
      if (this.src !== imageUrl) {
        this.src = imageUrl;
      }
    };
    
    imgWrapper.appendChild(img);
    return imgWrapper;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Always update mobile cart overlay when cart contents change
    // This ensures the overlay is updated every time an item is added
    this.updateMobileCartOverlay(parsedState).catch(err => {
      console.error('Error updating mobile cart overlay:', err);
      // If update fails, try to fetch fresh cart data
      this.initializeMobileCartOverlay();
    });

    // On mobile, show success banner before opening drawer
    if (this.isMobile) {
      this.showMobileSuccessBanner();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-section',
        section: 'cart-section',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-section',
        section: 'cart-section',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Prev/next arrows for the "Complete your routine" scroller in the cart drawer.
// A custom element so it re-binds itself whenever the drawer HTML is re-rendered.
if (!customElements.get('cart-recommendations-slider')) {
  customElements.define(
    'cart-recommendations-slider',
    class CartRecommendationsSlider extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('.cart-drawer__recommendations-scroller');
        this.prevButton = this.querySelector('[data-direction="prev"]');
        this.nextButton = this.querySelector('[data-direction="next"]');
        if (!this.scroller || !this.prevButton || !this.nextButton) return;

        this.onClick = (event) => this.scrollByCard(event.currentTarget.dataset.direction === 'next' ? 1 : -1);
        this.onScroll = () => this.updateButtons();

        this.prevButton.addEventListener('click', this.onClick);
        this.nextButton.addEventListener('click', this.onClick);
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

        // Drawer is hidden (zero width) until opened, so re-check whenever its size changes
        this.resizeObserver = new ResizeObserver(() => this.updateButtons());
        this.resizeObserver.observe(this.scroller);
        this.updateButtons();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.scroller) this.scroller.removeEventListener('scroll', this.onScroll);
      }

      scrollByCard(direction) {
        var card = this.scroller.querySelector('.cart-drawer__recommendation-card');
        var gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
        var step = card ? card.offsetWidth + gap : this.scroller.clientWidth;
        this.scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      updateButtons() {
        var maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
        var hasOverflow = maxScroll > 1;

        this.prevButton.hidden = !hasOverflow;
        this.nextButton.hidden = !hasOverflow;
        if (!hasOverflow) return;

        this.prevButton.disabled = this.scroller.scrollLeft <= 1;
        this.nextButton.disabled = this.scroller.scrollLeft >= maxScroll - 1;
      }
    }
  );
}

// Free tier gifts are now display-only in the drawer (GoKwik adds the real free products at
// checkout). This removes any `_free_gift` lines an earlier version of the theme added to the
// cart, so they can't be sent to checkout. Runs once per page load.
(function () {
  async function removeLegacyFreeGiftLines() {
    try {
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      const updates = {};
      cart.items.forEach((item) => {
        if (item.properties && item.properties._free_gift) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) return;

      await fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates }),
      });
      const drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems && typeof drawerItems.onCartUpdate === 'function') drawerItems.onCartUpdate();
    } catch (e) {
      console.error('Free gift cleanup failed', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeLegacyFreeGiftLines);
  } else {
    removeLegacyFreeGiftLines();
  }
})();
